import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {Alert, Button, Col, Row, Space} from "antd";
import {StyledServiceButtonWrapper} from "../../../../styles/onboarding/onboarding";
import {useNavigate} from "react-router-dom";
import {
  editStoreServiceBasicFormStepFormValidationSchema,
  EditStoreServiceBasicFormStepFormValues,
} from "../schema-validation";
import {EditStoreServiceFormValues} from "../types";
import {ServiceFormLayout} from "../../../../calls/useCases/createService/service-form-layout";
import {OfferingDeleteButton} from "../../../../components/offering-form/general/delete-button";
import {useEffect, useMemo} from "react";
import {EditStoreServiceOfferingForm} from "../../../../components/offering-form/services";
import {
  useCoverImagesWithFallback,
  mapDomainToEmbedded,
} from "../../../../features/media";
import {IS_PWYW_ENABLED} from "../../../../services/config";
import {useGetStoreServiceContext} from "../context/get-store-service";
import {useEditStoreServiceHook} from "../hooks/edit-store-service";
import {useDeleteStoreServiceHook} from "../hooks/delete-store-service";
import {toSavedEditStoreServiceFormValues} from "../saved-form-values";

export function EditStoreServiceBasicForm() {
  const navigate = useNavigate();
  const {
    state: {storeServiceData, portfolioImages},
  } = useGetStoreServiceContext();
  const {editStoreService, isEditingStoreService} = useEditStoreServiceHook();
  const {deleteStoreService} = useDeleteStoreServiceHook(
    storeServiceData.productService.id
  );

  // Fetch cover images via v2 with fallback to embedded data
  const {coverImages} = useCoverImagesWithFallback({
    offeringType: "product-service",
    offeringId: storeServiceData.productService.id,
    embedded: storeServiceData.productService.cover_images,
  });

  // Memoize to prevent infinite loops in useEffect
  const coverImagesForForm = useMemo(
    () => mapDomainToEmbedded(coverImages),
    [coverImages]
  );

  const formMethods = useForm<EditStoreServiceBasicFormStepFormValues>({
    resolver: yupResolver(editStoreServiceBasicFormStepFormValidationSchema),
    mode: "onSubmit",
    defaultValues: {
      ...toSavedEditStoreServiceFormValues(storeServiceData, IS_PWYW_ENABLED),
      cover_images: storeServiceData.productService.cover_images,
      portfolio_images: portfolioImages,
    },
  });

  useEffect(() => {
    formMethods.setValue("cover_images", coverImagesForForm);
    if (formMethods.watch("cover_images").length > 0) {
      formMethods.clearErrors("cover_images");
    } else {
      formMethods.trigger("cover_images");
    }
  }, [formMethods, coverImagesForForm]);

  // console.log({watx: formMethods.watch()});

  const isFormError = Object.keys(formMethods.formState.errors).length > 0;
  const isGeneralInformationError =
    formMethods.formState.errors.name || formMethods.formState.errors.description;
  const isCoverImagesError = formMethods.formState.errors.cover_images;
  const isPortfolioImagesError = formMethods.formState.errors.portfolio_images;
  const isDeliveryTimeFrameError =
    formMethods.formState.errors.deliveryTimeFrameUnit ||
    formMethods.formState.errors.deliveryTimeFrameValue;
  const isPricingError =
    formMethods.formState.errors.priceType || formMethods.formState.errors.currencies;
  const isSettingsError =
    formMethods.formState.errors.hideFromProfile ||
    formMethods.formState.errors.isUrlRedirect ||
    formMethods.formState.errors.redirectAfterPurchase ||
    formMethods.formState.errors.telegramGroupId ||
    formMethods.formState.errors.requestCustomerPhoneNumber ||
    formMethods.formState.errors.limitUnitsPerTransaction ||
    formMethods.formState.errors.purchaseLimit ||
    formMethods.formState.errors.url_redirect;

  // console.log({editformData: storeServiceData});

  function onSubmit(values: EditStoreServiceBasicFormStepFormValues) {
    // console.log({values});
    const payload: EditStoreServiceFormValues = {
      deliveryTimeFrameUnit: values.deliveryTimeFrameUnit,
      deliveryTimeFrameValue: values.deliveryTimeFrameValue,
      bookingQuestions: storeServiceData.productService.bookingQuestions
        ? storeServiceData.productService.bookingQuestions.map((q) => {
            if (
              q.answer_type === "checkbox" ||
              q.answer_type === "dropdown" ||
              q.answer_type === "radio-button"
            ) {
              return {
                question: q.question,
                required: q.required,
                answerType: q.answer_type,
                options: q.options.map((o) => ({
                  optionLabel: o,
                })),
              };
            } else {
              return {
                question: q.question,
                required: q.required,
                answerType: q.answer_type,
                options: [
                  {
                    optionLabel: "",
                  },
                ],
              };
            }
          })
        : [],
      currencies: values.currencies,
      description: values.description,
      hideFromProfile: values.hideFromProfile,
      id: storeServiceData.productService.id,
      isUrlRedirect: values.isUrlRedirect,
      name: values.name,
      priceType: values.priceType,
      requestCustomerPhoneNumber: values.requestCustomerPhoneNumber,
      limitUnitsPerTransaction: values.limitUnitsPerTransaction,
      purchaseLimit: values.purchaseLimit,
      url_redirect: values.url_redirect,
      redirectAfterPurchase: values.redirectAfterPurchase,
      telegramGroupId: values.telegramGroupId,
      enable_currency_conversion: true,
      selected_currencies: values.selected_currencies || [],
      // variants: values.variants,
    };
    editStoreService(payload);
  }

  console.log({
    editwatch: formMethods.watch(),
    editerrors: formMethods.formState.errors,
    isDeliveryTimeFrameError: !!isDeliveryTimeFrameError,
  });
  return (
    <ServiceFormLayout>
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(onSubmit)} autoComplete="off">
          <Space direction="vertical" size={16} style={{display: "flex"}}>
            <EditStoreServiceOfferingForm
              isGeneralInformationError={!!isGeneralInformationError}
              isPricingError={!!isPricingError}
              isSettingsError={!!isSettingsError}
              isCoverImagesError={!!isCoverImagesError}
              isDeliveryTimeFrameError={!!isDeliveryTimeFrameError}
              isPortfolioImagesError={!!isPortfolioImagesError}
            />
            {isFormError && (
              <Alert
                message="Please fill in the required fields above, outlined in red"
                type="warning"
              />
            )}

            <Row gutter={[0, 16]}>
              <Col span={24} sm={12}>
                <StyledServiceButtonWrapper>
                  <Button
                    className="secondary-btn"
                    onClick={() => {
                      navigate("/products?activeTab=services");
                    }}
                    block
                    size="small"
                    type="default"
                  >
                    Back
                  </Button>
                  <Button
                    block
                    type="primary"
                    htmlType={"submit"}
                    size="small"
                    disabled={
                      Object.keys(formMethods.formState.errors).length > 0 ||
                      Object.values(formMethods.watch()).length < 1
                    }
                    loading={isEditingStoreService}
                  >
                    Update service
                  </Button>
                  <OfferingDeleteButton
                    cancelText="Exit"
                    content="Are you sure you want to delete this service? This action cannot be undone."
                    okText="Delete service"
                    onOk={deleteStoreService}
                    title="Confirm service deletion"
                  />
                </StyledServiceButtonWrapper>
              </Col>
            </Row>
          </Space>
        </form>
      </FormProvider>
    </ServiceFormLayout>
  );
}
